import React, { ChangeEvent, useEffect, useState, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Swap } from "@app/components/common/icons";
import { CurrencyInput } from "@app/components/common/currency-converter/components/currency-input/CurrencyInput";
import { CurrencyInfo } from "@app/components/common/currency-converter/components/currency-info/CurrencyInfo";
import { useCurrencyRates } from "@app/components/common/currency-converter/useCurrencyRates";
import { calculateConverterAmount } from "@app/components/common/currency-converter/helpers";
import { Currency } from "@app/types/currencies";

export const Converter: React.FC = () => {
  const { t } = useTranslation("currency-converter");
  const [baseAmount, setBaseAmount] = useState<string>("1000");
  const [baseCurrency, setBaseCurrency] = useState(Currency.CHF);
  const [convertCurrency, setConvertCurrency] = useState(Currency.EUR);
  const rates = useCurrencyRates();

  const exchangeRate = useMemo(
    () => rates[baseCurrency].rates[convertCurrency],
    [rates, baseCurrency, convertCurrency],
  );

  const [convertedAmount, setConvertedAmount] = useState<string>(
    calculateConverterAmount(baseAmount, exchangeRate),
  );

  const onAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBaseAmount(event.target.value);

    setConvertedAmount(
      calculateConverterAmount(event.target.value, exchangeRate),
    );
  };

  const onConvertetAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    setConvertedAmount(event.target.value);

    setBaseAmount(
      calculateConverterAmount(event.target.value, 1 / exchangeRate),
    );
  };

  const onCurrencySelect =
    (setter: React.Dispatch<React.SetStateAction<Currency>>) =>
    (event: React.MouseEvent) =>
      setter(event.currentTarget.getAttribute("data-value") as Currency);

  const onSwap = () => {
    setBaseCurrency(convertCurrency);
    setConvertCurrency(baseCurrency);
  };

  useEffect(() => {
    setConvertedAmount(calculateConverterAmount(baseAmount, exchangeRate));
  }, [exchangeRate]);

  return (
    <div
      className={classNames(
        "flex flex-col gap-15 rounded-lg bg-grey-light px-9 py-15",
        "lg:min-w-[420px] lg:py-20",
        "xl:min-w-[460px] xl:px-15",
      )}
    >
      <div className="flex flex-col gap-5">
        <CurrencyInput
          label={t("from")}
          value={baseAmount}
          currency={baseCurrency}
          onChange={onAmountChange}
          onCurrencyChange={onCurrencySelect(setBaseCurrency)}
        />

        <div className="flex flex-row items-center justify-center">
          <div
            className={classNames(
              "flex h-15 w-15 rotate-90 cursor-pointer flex-row items-center justify-center rounded-full bg-divider",
              "hover:opacity-70",
            )}
            onClick={onSwap}
          >
            <Swap />
          </div>
        </div>

        <CurrencyInput
          label={t("to")}
          value={convertedAmount}
          currency={convertCurrency}
          onChange={onConvertetAmountChange}
          onCurrencyChange={onCurrencySelect(setConvertCurrency)}
        />
      </div>

      <CurrencyInfo
        baseCurrency={baseCurrency}
        convertCurrency={convertCurrency}
        ratesInfo={rates}
      />
    </div>
  );
};
